/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { css } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import type { ActionProperties, DeploymentActionResource, DeploymentStepResource, ProcessTemplateResource, GitRefResource } from "@octopusdeploy/octopus-server-client";
import { Permission, StartTrigger, SpecialVariables } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React, { useState } from "react";
import { getProcessTemplateVersionMaskFromAction } from "~/areas/projects/components/Process/ProcessTemplates/processTemplateId";
import Roles from "~/components/Actions/Roles";
import type { ActionPlugin } from "~/components/Actions/pluginRegistry";
import pluginRegistry from "~/components/Actions/pluginRegistry";
import Chip from "~/components/Chips/Chip";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { StepRolling } from "~/components/Images/Process/StepRolling";
import InternalLink from "~/components/Navigation/InternalLink/index";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import getActionLogoUrl from "../../getActionLogoUrl";
import { useProcessContext } from "../Contexts/ProcessContext";
import { useProcessErrorSelectors } from "../Contexts/ProcessErrors/ProcessErrorsContext";
import { useProcessWarningSelectors } from "../Contexts/ProcessWarnings/ProcessWarningsContext";
import type { ProcessListLayoutLoaderLookupData } from "../ProcessListLayoutLoader";
import type { StoredStep, StoredAction, ProcessIdentifier } from "../types";
import { isRunbookProcessIdentifier, isDeploymentProcessIdentifier } from "../types";
import ProcessListItem from "./ProcessListItem";
import processListItemStyles from "./ProcessListItem.module.less";
interface ProcessListPageListItemForProcessTemplateProps {
    lookups: ProcessListLayoutLoaderLookupData;
    step: StoredStep;
    containingProcessTemplateAction: StoredAction;
    doBusyTask: DoBusyTask;
    processTemplate: ProcessTemplateResource;
    gitRefResource: GitRefResource | undefined;
}
const ProcessListPageListItemForProcessTemplate: React.FC<ProcessListPageListItemForProcessTemplateProps> = ({ lookups, step, containingProcessTemplateAction, doBusyTask, processTemplate, gitRefResource }) => {
    const processContext = useProcessContext();
    const { selectors } = processContext;
    const processErrorSelectors = useProcessErrorSelectors();
    const errors = processErrorSelectors.getActionErrors(containingProcessTemplateAction.Id, selectors);
    const processWarningSelectors = useProcessWarningSelectors();
    const warnings = processWarningSelectors.getActionWarnings(containingProcessTemplateAction.Id, selectors);
    const processTemplateVersionMask = getProcessTemplateVersionMaskFromAction(containingProcessTemplateAction);
    const feedViewPermissionGranted = isAllowed({ permission: Permission.FeedView, wildcard: true });
    const environmentsById = lookups.environmentsV2ById;
    const channelsById = lookups.channelsById;
    const stepNumber = selectors.getStepNumber(step.Id);
    const maxParallelism = step.Properties[SpecialVariables.Action.MaxParallelism];
    const showWindowSize = typeof maxParallelism === "string" && maxParallelism ? maxParallelism.length > 0 : false;
    const parentStepLabel = React.useMemo(() => showWindowSize ? (<span>Rolling deployment</span>) : (<span>
                    Multi-step deployment across
                    <br />
                    deployment targets
                </span>), [showWindowSize]);
    const rollingStepIcon = React.useMemo(() => <StepRolling height="2.6rem" className={processListItemStyles.stepIcon}/>, []);
    const stepErrors = processErrorSelectors.getStepErrors(step.Id);
    const stepWarnings = processWarningSelectors.getStepWarnings(step.Id);
    function getTags(action: DeploymentActionResource): string[] {
        return action.TenantTags;
    }
    return (<div className={styles.processTemplateContainer} key={step.Id}>
            <InternalLink to={selectors.getStepDetailsUrl(gitRefResource, step.Id, containingProcessTemplateAction.Id)}>
                <div className={styles.processTemplateHeading}>{`Process Template: ${processTemplate.Name}`}</div>
            </InternalLink>
            {processTemplate.Steps.map((deploymentStep, index) => {
            const actionNumber = index + 1;
            if (deploymentStep.Actions.length === 1) {
                const processTemplateAction = deploymentStep.Actions[0];
                return (<ProcessListItem actionType={processTemplateAction.ActionType} logoUrl={getActionLogoUrl(processTemplateAction)} environmentsLookup={environmentsById
                        ? processTemplateAction.Environments.map((key) => ({
                            Key: key,
                            Environment: environmentsById[key],
                        }))
                        : undefined} excludedEnvironmentsLookup={environmentsById
                        ? processTemplateAction.ExcludedEnvironments.map((key) => ({
                            Key: key,
                            Environment: environmentsById[key],
                        }))
                        : undefined} environmentsVariable={processTemplateAction.EnvironmentsVariable} excludedEnvironmentsVariable={processTemplateAction.ExcludedEnvironmentsVariable} channelsLookup={channelsById
                        ? processTemplateAction.Channels.map((key) => ({
                            Key: key,
                            Channel: channelsById[key],
                        }))
                        : undefined} channelsVariable={processTemplateAction.ChannelsVariable} tags={getTags(processTemplateAction)} tenantTagsVariable={processTemplateAction.TenantTagsVariable} isDisabled={processTemplateAction.IsDisabled || selectors.isActionDisabled(containingProcessTemplateAction.Id)} notes={processTemplateAction.Notes} actionErrors={errors} actionWarnings={warnings} index={`${stepNumber}.${actionNumber}.`} detailsUrl={getProcessTemplateStepDetailsUrl(processContext.state.processIdentifier, gitRefResource, containingProcessTemplateAction.Id)} isParentGroup={false} name={processTemplateAction.Name} isRunInParallelWithLast={deploymentStep.StartTrigger === StartTrigger.StartWithPrevious && !(actionNumber === 1)} key={deploymentStep.Id}>
                            <div className={processListItemStyles.summaryContainer}>
                                <ProcessTemplateStepSummary step={deploymentStep} action={processTemplateAction} lookups={lookups} isChildAction={false}/>
                                {processTemplateAction.Container.Image && processTemplateAction.Container.FeedId && processTemplateAction.Properties["Octopus.Action.RunOnServer"] === "true" ? (<span style={{ paddingLeft: "0.1em" }}>
                                        <Chip description={feedViewPermissionGranted ? `Container Image: ${processTemplateAction.Container.Image}` : undefined}>Runs in a container</Chip>
                                    </span>) : ("")}
                            </div>
                        </ProcessListItem>);
            }
            else {
                return (<div key={deploymentStep.Id} className={processListItemStyles.group}>
                            <ProcessListItem actionType={parentStepLabel} icon={rollingStepIcon} actionErrors={stepErrors} actionWarnings={stepWarnings} detailsUrl={getProcessTemplateStepDetailsUrl(processContext.state.processIdentifier, gitRefResource, containingProcessTemplateAction.Id)} isParentGroup={true} index={`${stepNumber}.${actionNumber}.`} name={deploymentStep.Name} isRunInParallelWithLast={deploymentStep.StartTrigger === StartTrigger.StartWithPrevious && !selectors.isFirstStep(deploymentStep.Id)} isDisabled={isParentDisabled(deploymentStep) || selectors.isActionDisabled(containingProcessTemplateAction.Id)} notes={null} key={deploymentStep.Id}>
                                {deploymentStep.Properties[SpecialVariables.Action.MaxParallelism] ? <span>Rolling deployment</span> : <span>Multi-step deployment</span>}
                                &nbsp;across deployment targets with&nbsp;
                                <Roles rolesAsCSV={deploymentStep.Properties[SpecialVariables.Action.TargetRoles] as string}/>
                            </ProcessListItem>
                            {deploymentStep.Actions.map((processTemplateAction, childIndex) => {
                        const childNumber = childIndex + 1;
                        return (<ProcessListItem key={processTemplateAction.Id} actionType={processTemplateAction.ActionType} logoUrl={getActionLogoUrl(processTemplateAction)} environmentsLookup={environmentsById
                                ? processTemplateAction.Environments.map((key) => ({
                                    Key: key,
                                    Environment: environmentsById[key],
                                }))
                                : undefined} excludedEnvironmentsLookup={environmentsById
                                ? processTemplateAction.ExcludedEnvironments.map((key) => ({
                                    Key: key,
                                    Environment: environmentsById[key],
                                }))
                                : undefined} environmentsVariable={processTemplateAction.EnvironmentsVariable} excludedEnvironmentsVariable={processTemplateAction.ExcludedEnvironmentsVariable} channelsLookup={channelsById
                                ? processTemplateAction.Channels.map((key) => ({
                                    Key: key,
                                    Channel: channelsById[key],
                                }))
                                : undefined} channelsVariable={processTemplateAction.ChannelsVariable} tags={getTags(processTemplateAction)} tenantTagsVariable={processTemplateAction.TenantTagsVariable} isDisabled={processTemplateAction.IsDisabled || selectors.isActionDisabled(containingProcessTemplateAction.Id)} notes={processTemplateAction.Notes} actionErrors={errors} actionWarnings={warnings} index={`${stepNumber}.${actionNumber}.${childNumber}.`} detailsUrl={getProcessTemplateStepDetailsUrl(processContext.state.processIdentifier, gitRefResource, containingProcessTemplateAction.Id)} isParentGroup={false} name={processTemplateAction.Name} isRunInParallelWithLast={false}>
                                        <div className={processListItemStyles.summaryContainer}>
                                            <ProcessTemplateStepSummary step={deploymentStep} action={processTemplateAction} lookups={lookups} isChildAction={true}/>
                                            {processTemplateAction.Container.Image && processTemplateAction.Container.FeedId && processTemplateAction.Properties["Octopus.Action.RunOnServer"] === "true" ? (<span style={{ paddingLeft: "0.1em" }}>
                                                    <Chip description={feedViewPermissionGranted ? `Container Image: ${processTemplateAction.Container.Image}` : undefined}>Runs in a container</Chip>
                                                </span>) : ("")}
                                        </div>
                                    </ProcessListItem>);
                    })}
                        </div>);
            }
        })}
        </div>);
};
ProcessListPageListItemForProcessTemplate.displayName = "ProcessListPageListItemForProcessTemplate"
function isParentDisabled(step: DeploymentStepResource): boolean {
    return step.Actions.every((action: DeploymentActionResource) => action.IsDisabled);
}
export const getProcessTemplateStepDetailsUrl = (processIdentifier: ProcessIdentifier, gitRef: GitRefResource | undefined, actionId: string | undefined) => {
    if (isDeploymentProcessIdentifier(processIdentifier)) {
        return gitRef
            ? links.branchDeploymentProcessEditorPage.generateUrl({ spaceId: processIdentifier.spaceId, projectSlug: processIdentifier.projectSlug, branchName: gitRef.CanonicalName }, { actionId })
            : links.deploymentProcessEditorPage.generateUrl({ spaceId: processIdentifier.spaceId, projectSlug: processIdentifier.projectSlug }, { actionId });
    }
    else if (isRunbookProcessIdentifier(processIdentifier)) {
        return links.runbookProcessEditorPage.generateUrl({ spaceId: processIdentifier.spaceId, projectSlug: processIdentifier.projectSlug, runbookId: processIdentifier.runbookId, processId: processIdentifier.id }, { actionId });
    }
    else {
        throw new Error(`Process templates aren't allowed here. Unknown process identifier ${processIdentifier}`);
    }
};
const styles = {
    processTemplateHeading: css({
        display: "flex",
        gap: space[16],
        padding: space[16],
        color: "var(--linkText)",
        fontWeight: "500",
        fontSize: "1rem",
        borderTop: "0.1rem solid var(--dividerLight)",
        borderBottom: "0.0625rem solid var(--dividerLight)",
        position: "relative",
        zIndex: "0",
        backgroundColor: "var(--secondaryBackground)",
        ":hover": {
            backgroundColor: "var(--hover)",
        },
    }),
    processTemplateContainer: css({
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--secondaryBackground)",
        borderBottom: "0.1rem solid var(--dividerLight)",
    }),
};
interface ProcessTemplateStepSummaryProps {
    step: DeploymentStepResource;
    action: DeploymentActionResource;
    lookups: ProcessListLayoutLoaderLookupData;
    isChildAction: boolean;
}
export function ProcessTemplateStepSummary({ step, action, lookups, isChildAction }: ProcessTemplateStepSummaryProps) {
    const [actionPlugin, setActionPlugin] = useState<ActionPlugin<ActionProperties, Partial<Record<string, string>>> | undefined>();
    const { doBusyTask, status: doBusyTaskStatus } = useLegacyDoBusyTask();
    useDoBusyTaskEffect(doBusyTask, async () => {
        const actionPlugin = await pluginRegistry.getAction(action.ActionType, action.StepPackageVersion);
        setActionPlugin(actionPlugin);
    }, []);
    if (!actionPlugin) {
        return null;
    }
    return (<>
            {actionPlugin.summary(action.Properties, isChildAction ? "" : (step.Properties[SpecialVariables.Action.TargetRoles] as string), action.Packages, action.WorkerPoolId ? lookups.workerPoolsById[action.WorkerPoolId] : undefined, action.WorkerPoolVariable ?? undefined, action.GitDependencies ?? undefined)}
        </>);
}
export default ProcessListPageListItemForProcessTemplate;
